import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { PrinterInstance } from '../../../../generated/generated-types';
import { RemotePrinterDropdownComponent } from '../../../printing/components/remote-printer-dropdown/remote-printer-dropdown.component';
import {
  parseRemotePrintOptions,
  remotePrintTargetChoiceId,
  remotePrintTargetFromChoice,
  resolveRemotePrintTarget,
  sameRemotePrintTarget,
  withRemotePrintTarget,
} from '../../../printing/services/remote-print-options.util';

const OPTIONS_KEY = 'outputTypeOptions';

/**
 * Printer or printer group for a REMOTE_PRINT report trigger.
 * The field has no key of its own. It writes `printerGroup` or `remotePrinterId` into the trigger's
 * `outputTypeOptions` JSON, so nobody has to type a `group:` prefix and other options stay in the JSON field.
 */
@Component({
  selector: 'app-report-trigger-remote-printer',
  standalone: true,
  imports: [RemotePrinterDropdownComponent, TranslatePipe],
  templateUrl: './report-trigger-remote-printer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col gap-1 w-full',
    '(focusin)': 'markUserInteraction()',
    '(pointerdown)': 'markUserInteraction()',
  },
})
export class ReportTriggerRemotePrinterComponent extends FieldType implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectedId = signal<string>('');
  readonly invalidOptions = signal(false);

  /** The dropdown also emits while it loads the stored choice. Only a pick by the user writes the JSON. */
  private userInteracted = false;

  ngOnInit(): void {
    this.syncFromOptions();
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncFromOptions());
  }

  markUserInteraction(): void {
    this.userInteracted = true;
  }

  onChoice(choice: PrinterInstance | PrinterInstance[] | null | undefined): void {
    if (!this.userInteracted || Array.isArray(choice)) {
      return;
    }
    const current = parseRemotePrintOptions(this.currentOptionsValue());
    if (current == null) {
      // Do not overwrite JSON the user is still typing.
      return;
    }
    const target = remotePrintTargetFromChoice(choice);
    if (sameRemotePrintTarget(target, resolveRemotePrintTarget(current))) {
      return;
    }
    const next = withRemotePrintTarget(current, target);
    const value = Object.keys(next).length ? JSON.stringify(next, null, 2) : null;
    const control = this.form.get(OPTIONS_KEY);
    if (control) {
      control.setValue(value);
      control.markAsDirty();
    } else if (this.model) {
      this.model[OPTIONS_KEY] = value;
    }
    this.form.markAsDirty();
  }

  private currentOptionsValue(): unknown {
    const control = this.form.get(OPTIONS_KEY);
    return control ? control.value : this.model?.[OPTIONS_KEY];
  }

  private syncFromOptions(): void {
    const parsed = parseRemotePrintOptions(this.currentOptionsValue());
    this.invalidOptions.set(parsed == null);
    if (parsed == null) {
      return;
    }
    const id = remotePrintTargetChoiceId(parsed) ?? '';
    if (id !== this.selectedId()) {
      this.selectedId.set(id);
    }
  }
}
